/**
 * @file VarianceSummary component
 * @description Displays an inline summary of schedule variance between baseline
 * and current dates. Color-coded: red for slippage, green for acceleration,
 * gray for on-track.
 */

interface VarianceSummaryProps {
  baselineStart: string;
  baselineEnd: string;
  currentStart: string;
  currentEnd: string;
}

function daysDiff(a: string, b: string): number {
  const msPerDay = 86400000;
  const dateA = new Date(a);
  const dateB = new Date(b);
  if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
    return 0;
  }
  return Math.round((dateB.getTime() - dateA.getTime()) / msPerDay);
}

function varianceLabel(days: number): { text: string; colorClass: string } {
  if (days > 0) {
    return { text: `+${days}d`, colorClass: 'text-red-600 dark:text-red-400' };
  }
  if (days < 0) {
    return { text: `${days}d`, colorClass: 'text-green-600 dark:text-green-400' };
  }
  return { text: 'On track', colorClass: 'text-gray-500 dark:text-gray-400' };
}

export function VarianceSummary({
  baselineStart,
  baselineEnd,
  currentStart,
  currentEnd,
}: VarianceSummaryProps) {
  const startVariance = daysDiff(baselineStart, currentStart);
  const endVariance = daysDiff(baselineEnd, currentEnd);

  const startLabel = varianceLabel(startVariance);
  const endLabel = varianceLabel(endVariance);

  return (
    <div className="flex items-center gap-4 text-xs">
      <span className="text-gray-500 dark:text-gray-400">
        Start:{' '}
        <span className={`font-medium ${startLabel.colorClass}`}>{startLabel.text}</span>
      </span>
      <span className="text-gray-500 dark:text-gray-400">
        End:{' '}
        <span className={`font-medium ${endLabel.colorClass}`}>{endLabel.text}</span>
      </span>
    </div>
  );
}

export default VarianceSummary;
